import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GCBadgeSVG, SCBadgeSVG } from "./CurrencyBadge";
import { cn } from "@/lib/utils";
import { Clock, Sparkles, Gift } from 'lucide-react';

export type OfferType = 'special' | 'scheduled' | 'group' | 'welcome';

interface CompactOfferCardProps {
  type: OfferType;
  title?: string;
  gcAmount: number;  // Total GC (base + bonus)
  scAmount: number;  // Total SC (base + bonus)
  gcBaseAmount?: number;  // Base GC without bonus
  scBaseAmount?: number;  // Base SC without bonus
  price: number;
  originalPrice?: number | null;
  badgeText?: string | null;
  badgeColor?: string;
  cadenceLabel?: string;
  expiresAt?: string | null;  // ISO date string for expiration
  purchasesRemaining?: number;
  maxPurchases?: number;
  canPurchase?: boolean;
  isWelcomeOffer?: boolean;  // Flag for welcome offer styling
  onPurchase: () => void;
  formatCoins: (amount: number | string | null | undefined) => string;
  formatPrice: (price: number | string | null | undefined) => string;
}

// Helper to format time remaining
const formatTimeRemaining = (targetDate: Date): { text: string; isUrgent: boolean } | null => {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  
  if (diff <= 0) return null; // Expired
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  // More than 24 hours - show days and hours
  if (days > 0) {
    return { text: `${days}d ${hours}h`, isUrgent: false };
  }
  
  // Less than 24 hours - show countdown with seconds (HH:MM:SS format)
  const pad = (n: number) => n.toString().padStart(2, '0');
  return { 
    text: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
    isUrgent: hours < 1 // Extra urgent if less than 1 hour
  };
};

export const CompactOfferCard: React.FC<CompactOfferCardProps> = ({
  type,
  title,
  gcAmount,
  scAmount,
  gcBaseAmount,
  scBaseAmount,
  price,
  originalPrice,
  badgeText,
  badgeColor,
  cadenceLabel,
  expiresAt,
  purchasesRemaining,
  maxPurchases,
  canPurchase = true,
  isWelcomeOffer = false,
  onPurchase,
  formatCoins,
  formatPrice,
}) => {
  const hasDiscount = originalPrice && originalPrice > price;
  const discountPercent = hasDiscount ? Math.round((1 - price / originalPrice) * 100) : 0;
  
  // Check if this is a welcome offer (either by prop or cadence label)
  const isWelcome = isWelcomeOffer || cadenceLabel?.toLowerCase().includes('welcome');

  // Timer state for countdown
  const [timerData, setTimerData] = useState<{ text: string; isUrgent: boolean } | null>(null);
  
  useEffect(() => {
    if (!expiresAt) {
      setTimerData(null);
      return;
    }
    
    const targetDate = new Date(expiresAt);
    
    // Initial calculation
    setTimerData(formatTimeRemaining(targetDate));
    
    // Update every second
    const interval = setInterval(() => {
      const remaining = formatTimeRemaining(targetDate);
      setTimerData(remaining);
      
      // Clear interval if expired
      if (!remaining) {
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  // Calculate bonus percentages for both GC and SC
  const gcBonusAmount = gcBaseAmount ? gcAmount - gcBaseAmount : 0;
  const scBonusAmount = scBaseAmount ? scAmount - scBaseAmount : 0;
  
  const gcBonusPercent = gcBaseAmount && gcBaseAmount > 0 
    ? Math.round((gcBonusAmount / gcBaseAmount) * 100) 
    : 0;
  const scBonusPercent = scBaseAmount && scBaseAmount > 0 
    ? Math.round((scBonusAmount / scBaseAmount) * 100) 
    : 0;
  
  // Use the higher bonus percentage between GC and SC
  const bonusPercent = Math.max(gcBonusPercent, scBonusPercent);
  
  // Determine which promo badge to show (only if > 10%)
  // Prefer discount over bonus if both are significant
  const showDiscountBadge = discountPercent > 10;
  const showBonusBadge = !showDiscountBadge && bonusPercent > 10;

  const getTypeStyle = () => {
    // Welcome offers get premium gold styling regardless of type
    if (isWelcome) {
      return {
        gradient: 'from-amber-900/90 via-yellow-800/80 to-amber-900/90',
        border: 'border-yellow-400/70 hover:border-yellow-300',
        accent: 'from-yellow-300 via-amber-200 to-yellow-300',
        glow: 'shadow-yellow-500/40 hover:shadow-yellow-400/60',
        badge: '🎁 WELCOME',
        badgeBg: 'bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-400 text-amber-900',
        btnGradient: 'from-yellow-500 via-amber-400 to-yellow-500 hover:from-yellow-400 hover:via-amber-300 hover:to-yellow-400',
        btnText: 'text-amber-900',
        isWelcome: true,
      };
    }
    
    switch (type) {
      case 'special':
        return {
          gradient: 'from-amber-950/80 via-orange-900/60 to-amber-800/50',
          border: 'border-amber-500/50 hover:border-amber-400/80',
          accent: 'from-amber-400 via-yellow-300 to-amber-400',
          glow: 'shadow-amber-500/20 hover:shadow-amber-500/40',
          badge: '⭐ SPECIAL',
          badgeBg: 'bg-gradient-to-r from-amber-500 to-yellow-400 text-amber-900',
          btnGradient: 'from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:via-yellow-300 hover:to-amber-400',
          btnText: 'text-amber-900',
          isWelcome: false,
        };
      case 'scheduled':
        // Format cadence label with appropriate emoji
        const formatCadenceLabel = (label?: string) => {
          if (!label) return '⭐ Special';
          const lower = label.toLowerCase().trim();
          
          // Exact matches first
          if (lower === 'welcome') return '🎁 Welcome';
          if (lower === 'daily') return '📅 Daily';
          if (lower === 'weekly') return '📆 Weekly';
          if (lower === 'monthly') return '🗓️ Monthly';
          if (lower === 'birthday') return '🎂 Birthday';
          if (lower === 'holiday') return '🎄 Holiday';
          if (lower === 'one_time') return '⭐ One Time';
          if (lower === 'specific_days_of_week') return '📆 Weekly';
          if (lower === 'specific_days_of_month') return '🗓️ Monthly';
          
          // Partial matches as fallback
          if (lower.includes('welcome')) return '🎁 Welcome';
          if (lower.includes('daily')) return '📅 Daily';
          if (lower.includes('weekly') || lower.includes('week')) return '📆 Weekly';
          if (lower.includes('monthly') || lower.includes('month')) return '🗓️ Monthly';
          if (lower.includes('birthday')) return '🎂 Birthday';
          if (lower.includes('holiday')) return '🎄 Holiday';
          if (lower.includes('one_time') || lower.includes('one-time') || lower.includes('onetime')) return '⭐ One Time';
          
          // Default: capitalize first letter
          return `⭐ ${label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' ')}`;
        };
        return {
          gradient: 'from-slate-900/90 via-slate-800/70 to-slate-900/90',
          border: 'border-blue-500/40 hover:border-blue-400/70',
          accent: 'from-blue-400 via-cyan-300 to-blue-400',
          glow: 'shadow-blue-500/20 hover:shadow-blue-500/40',
          badge: formatCadenceLabel(cadenceLabel),
          badgeBg: 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white',
          btnGradient: 'from-blue-500 via-cyan-400 to-blue-500 hover:from-blue-400 hover:via-cyan-300 hover:to-blue-400',
          btnText: 'text-white',
          isWelcome: false,
        };
      case 'group':
        return {
          gradient: 'from-purple-950/80 via-purple-900/60 to-purple-800/50',
          border: 'border-purple-500/50 hover:border-purple-400/80',
          accent: 'from-purple-400 via-pink-300 to-purple-400',
          glow: 'shadow-purple-500/20 hover:shadow-purple-500/40',
          badge: '🎰 PICK ONE',
          badgeBg: 'bg-gradient-to-r from-purple-500 to-pink-400 text-white',
          btnGradient: 'from-purple-500 via-pink-400 to-purple-500 hover:from-purple-400 hover:via-pink-300 hover:to-purple-400',
          btnText: 'text-white',
          isWelcome: false,
        };
      default:
        return {
          gradient: 'from-muted via-muted/70 to-muted',
          border: 'border-border hover:border-border/80',
          accent: 'from-slate-400 via-slate-300 to-slate-400',
          glow: 'shadow-slate-500/20 hover:shadow-slate-500/40',
          badge: '⭐ SPECIAL',
          badgeBg: 'bg-gradient-to-r from-slate-500 to-slate-400 text-white',
          btnGradient: 'from-slate-500 via-slate-400 to-slate-500 hover:from-slate-400 hover:via-slate-300 hover:to-slate-400',
          btnText: 'text-white',
          isWelcome: false,
        };
    }
  };

  const style = getTypeStyle();

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all duration-300 group h-[250px] hover-card-lift",
      `bg-gradient-to-br ${style.gradient}`,
      `border-2 ${style.border}`,
      `shadow-lg ${style.glow}`,
      !canPurchase && "opacity-60",
      // Extra styling for welcome offers
      isWelcome && "ring-2 ring-yellow-400/50 ring-offset-2 ring-offset-transparent"
    )}>
      {/* Welcome offer animated border glow */}
      {isWelcome && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-400/20 via-amber-300/30 to-yellow-400/20 animate-pulse pointer-events-none" />
      )}
      
      {/* Top accent line with glow */}
      <div className={cn(
        "absolute top-0 left-0 right-0 opacity-80 group-hover:opacity-100 transition-opacity",
        `bg-gradient-to-r ${style.accent}`,
        isWelcome ? "h-2" : "h-1.5"
      )} />
      
      {/* Welcome offer sparkle decorations */}
      {isWelcome && (
        <>
          <Sparkles className="absolute top-3 right-3 h-5 w-5 text-yellow-300 animate-pulse" />
          <Sparkles className="absolute bottom-16 left-3 h-4 w-4 text-amber-300 animate-pulse" style={{ animationDelay: '0.5s' }} />
        </>
      )}
      
      {/* Shimmer effect - enhanced for welcome */}
      <div className={cn(
        "absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform pointer-events-none",
        isWelcome 
          ? "bg-gradient-to-r from-transparent via-yellow-200/20 to-transparent duration-700" 
          : "bg-gradient-to-r from-transparent via-white/5 to-transparent duration-1000"
      )} />

      <CardContent className="p-3 lg:p-4 h-full flex flex-col justify-between relative z-10">
        {/* Header row with badge */}
        <div className="flex items-start justify-between gap-1.5">
          <Badge className={cn(
            "font-black text-[9px] lg:text-xs px-2 lg:px-3 py-1 lg:py-1.5 shadow-md whitespace-nowrap",
            style.badgeBg
          )}>
            {badgeText || style.badge}
          </Badge>
          
          {/* Show discount OR bonus badge (whichever is more significant, >10%) */}
          {showDiscountBadge && (
            <Badge className="bg-gradient-to-r from-red-500 to-orange-400 text-white font-black text-[9px] lg:text-xs px-2 lg:px-3 py-1 lg:py-1.5 shadow-md animate-pulse">
              {discountPercent}% OFF
            </Badge>
          )}
          {showBonusBadge && (
            <Badge className="bg-gradient-to-r from-green-500 to-emerald-400 text-white font-black text-[9px] lg:text-xs px-2 lg:px-3 py-1 lg:py-1.5 shadow-md animate-pulse">
              +{bonusPercent}%
            </Badge>
          )}
        </div>

        {/* Currency display - grid so badges align vertically */}
        <div className="flex flex-col items-center justify-center py-2 flex-1">
          {(() => {
            const gcFormatted = formatCoins(gcAmount);
            const scFormatted = formatCoins(scAmount);
            // Determine text size based on longest formatted value
            // SC row includes " FREE" suffix, so we need to account for total width
            // 3 chars or less = text-4xl, 4 chars = text-3xl, 5+ chars = text-2xl
            const maxLen = Math.max(gcFormatted.length, scFormatted.length);
            const amountTextSize = maxLen <= 3 ? 'text-4xl' : maxLen <= 4 ? 'text-3xl' : 'text-2xl';
            const freeTextSize = maxLen <= 3 ? 'text-xl' : maxLen <= 4 ? 'text-lg' : 'text-base';
            const badgeSize = maxLen <= 3 ? 32 : maxLen <= 4 ? 28 : 24;
            const gapSize = maxLen <= 3 ? 'gap-x-3 gap-y-3' : maxLen <= 4 ? 'gap-x-2 gap-y-2' : 'gap-x-2 gap-y-1.5';
            
            return (
              <div className={cn("grid grid-cols-[auto_auto_1fr] items-center justify-items-start w-fit", gapSize)}>
                {/* GC row */}
                {scAmount > 0 && <span className="text-slate-500 text-xl invisible">+</span>}
                <GCBadgeSVG size={badgeSize} />
                <span className={cn(amountTextSize, "font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 drop-shadow-lg")}>
                  {gcFormatted}
                </span>
                
                {/* SC row */}
                {scAmount > 0 && (
                  <>
                    <span className="text-slate-500 text-xl">+</span>
                    <SCBadgeSVG size={badgeSize} />
                    <span className={cn(amountTextSize, "font-black text-green-400 whitespace-nowrap")}>
                      {scFormatted} <span className={cn(freeTextSize, "text-green-400/70")}>FREE</span>
                    </span>
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {/* Purchase limit indicator */}
        {maxPurchases && maxPurchases > 1 && purchasesRemaining !== undefined && (
          <div className="text-xs text-slate-400 text-center mb-1 font-medium">
            {purchasesRemaining} of {maxPurchases} remaining
          </div>
        )}

        {/* Expiration timer */}
        {timerData && (
          <div className={cn(
            "flex items-center justify-center gap-1.5 mb-2 py-1 px-2 rounded-lg",
            timerData.isUrgent 
              ? "bg-red-500/20 animate-pulse" 
              : "bg-amber-500/10"
          )}>
            <Clock className={cn(
              "h-3.5 w-3.5",
              timerData.isUrgent ? "text-red-400" : "text-amber-400"
            )} />
            <span className={cn(
              "text-xs font-semibold",
              timerData.isUrgent ? "text-red-400" : "text-amber-400"
            )}>
              {type === 'scheduled' ? 'Resets ' : 'Ends '}
              <span className="font-mono font-bold">{timerData.text}</span>
            </span>
          </div>
        )}

        {/* Purchase button */}
        {canPurchase ? (
          (() => {
            const priceFormatted = formatPrice(price);
            const originalFormatted = originalPrice ? formatPrice(originalPrice) : '';
            // Dynamic sizing only when showing both prices and they're long
            // "$1200.00" = 8 chars, "$999.99" = 7 chars = 15 total
            const hasBothPrices = hasDiscount && originalFormatted;
            const totalLen = hasBothPrices ? priceFormatted.length + originalFormatted.length : priceFormatted.length;
            // Only scale down when we have both prices AND total is long
            const needsSmaller = hasBothPrices && totalLen > 12;
            const priceTextSize = needsSmaller ? 'text-xl' : 'text-2xl';
            const originalTextSize = needsSmaller ? 'text-xs' : 'text-base';
            const gapSize = needsSmaller ? 'gap-2' : 'gap-3';
            
            return (
              <Button
                onClick={onPurchase}
                className={cn(
                  "w-full font-black py-3 rounded-xl text-lg h-14",
                  "transition-all duration-200 relative overflow-hidden group/btn",
                  `bg-gradient-to-r ${style.btnGradient}`,
                  style.btnText,
                  "shadow-lg hover:shadow-xl border-0"
                )}
              >
                <span className={cn("relative z-10 flex items-center justify-center", gapSize)}>
                  {hasDiscount && (
                    <span className={cn("line-through opacity-60", originalTextSize)}>
                      {originalFormatted}
                    </span>
                  )}
                  <span className={priceTextSize}>{priceFormatted}</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-500" />
              </Button>
            );
          })()
        ) : (
          <div className="text-center py-4 bg-muted/60 rounded-xl border border-border">
            <p className="text-sm text-muted-foreground font-semibold">Limit reached</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
